/**
 * 知识类视频模板合集 Compositions
 *
 * 205 个模板组件 × 多种预设 = 270+ Compositions
 *
 * 竖屏 (1080×1920)：
 *   - AcademicEducation (深蓝学术)
 *   - ProfessionalSkills (暗色科技)
 *   - SciencePopularization (深空数据)
 *   - LifeKnowledge (暖色卡片)
 *   - LifeMagazine (杂志排版)
 *   - MindsetGrowth (星空禅意)
 *   - MindsetCinematic (电影感)
 *   - Psychology (紫蓝曲线)
 *
 * 横屏 (1920×1080)：
 *   - AcademicChalkboard (黑板粉笔)
 *   - SkillsDashboard (仪表盘)
 *   - ScienceInfographic (信息图表)
 *   - PsychologyLab (实验室白底)
 *
 * @date 2026-03-18
 */

import React from 'react';
import { Composition, staticFile } from 'remotion';
import { AcademicEducationTemplate } from '../templates/knowledge/AcademicEducationTemplate';
import { ProfessionalSkillsTemplate } from '../templates/knowledge/ProfessionalSkillsTemplate';
import { SciencePopularizationTemplate } from '../templates/knowledge/SciencePopularizationTemplate';
import { LifeKnowledgeTemplate } from '../templates/knowledge/LifeKnowledgeTemplate';
import { MindsetGrowthTemplate } from '../templates/knowledge/MindsetGrowthTemplate';
import { PsychologyTemplate } from '../templates/knowledge/PsychologyTemplate';
import { AcademicChalkboardTemplate } from '../templates/knowledge/AcademicChalkboardTemplate';
import { SkillsDashboardTemplate } from '../templates/knowledge/SkillsDashboardTemplate';
import { ScienceInfographicTemplate } from '../templates/knowledge/ScienceInfographicTemplate';
import { LifeMagazineTemplate } from '../templates/knowledge/LifeMagazineTemplate';
import { MindsetCinematicTemplate } from '../templates/knowledge/MindsetCinematicTemplate';
import { PsychologyLabTemplate } from '../templates/knowledge/PsychologyLabTemplate';
import { AcademicSlideTemplate } from '../templates/knowledge/AcademicSlideTemplate';
import { SkillsChecklistTemplate } from '../templates/knowledge/SkillsChecklistTemplate';
import { LifeComparisonTemplate } from '../templates/knowledge/LifeComparisonTemplate';
import { MindsetMindmapTemplate } from '../templates/knowledge/MindsetMindmapTemplate';
import { PsychologyStoryTemplate } from '../templates/knowledge/PsychologyStoryTemplate';
import { AcademicAnimatedDiagram } from '../templates/knowledge/AcademicAnimatedDiagram';
import { SkillsCodeDemo } from '../templates/knowledge/SkillsCodeDemo';
import { ScienceDataViz } from '../templates/knowledge/ScienceDataViz';
import { LifeProcessFlow } from '../templates/knowledge/LifeProcessFlow';
import { MindsetGrowthTree } from '../templates/knowledge/MindsetGrowthTree';
import { PsychologySpectrum } from '../templates/knowledge/PsychologySpectrum';
import { PoetryInkTemplate } from '../templates/knowledge/PoetryInkTemplate';
import { CalligraphyArtTemplate } from '../templates/knowledge/CalligraphyArtTemplate';
import { ArtGalleryTemplate } from '../templates/knowledge/ArtGalleryTemplate';
import { MusicTheoryTemplate } from '../templates/knowledge/MusicTheoryTemplate';
import { HistoryTimelineTemplate } from '../templates/knowledge/HistoryTimelineTemplate';
import { FinanceDashboardTemplate } from '../templates/knowledge/FinanceDashboardTemplate';
import { AstronomyTemplate } from '../templates/knowledge/AstronomyTemplate';
import { PhilosophyDialecticTemplate } from '../templates/knowledge/PhilosophyDialecticTemplate';
import { HealthAnatomyTemplate } from '../templates/knowledge/HealthAnatomyTemplate';
import { LanguageCardTemplate } from '../templates/knowledge/LanguageCardTemplate';
import { GeographyMapTemplate } from '../templates/knowledge/GeographyMapTemplate';
import { LawExplainerTemplate } from '../templates/knowledge/LawExplainerTemplate';
import { ArchitectureBlueprintTemplate } from '../templates/knowledge/ArchitectureBlueprintTemplate';
import { CookingRecipeTemplate } from '../templates/knowledge/CookingRecipeTemplate';
import { PhotographyTemplate } from '../templates/knowledge/PhotographyTemplate';
import { SportsAnalysisTemplate } from '../templates/knowledge/SportsAnalysisTemplate';
import { MathVisualizationTemplate } from '../templates/knowledge/MathVisualizationTemplate';
import { EnvironmentClimateTemplate } from '../templates/knowledge/EnvironmentClimateTemplate';
import { AINetworkTemplate } from '../templates/knowledge/AINetworkTemplate';
import { ChemistryLabTemplate } from '../templates/knowledge/ChemistryLabTemplate';
import { BiologyDNATemplate } from '../templates/knowledge/BiologyDNATemplate';
import { FilmCinematicTemplate } from '../templates/knowledge/FilmCinematicTemplate';
import { ChessStrategyTemplate } from '../templates/knowledge/ChessStrategyTemplate';
import { ElectronicsCircuitTemplate } from '../templates/knowledge/ElectronicsCircuitTemplate';
import { ChocolateTemplate } from '../templates/knowledge/ChocolateTemplate';
import { MushroomTemplate } from '../templates/knowledge/MushroomTemplate';
import { RocketPropulsionTemplate } from '../templates/knowledge/RocketPropulsionTemplate';
import { LacquerwareTemplate } from '../templates/knowledge/LacquerwareTemplate';
import { GyroscopeTemplate } from '../templates/knowledge/GyroscopeTemplate';
import { MosaicTemplate } from '../templates/knowledge/MosaicTemplate';
import { DesalinationTemplate } from '../templates/knowledge/DesalinationTemplate';
import { BambooTemplate } from '../templates/knowledge/BambooTemplate';
import { SupplyChainTemplate } from '../templates/knowledge/SupplyChainTemplate';
import { DeepOceanTemplate } from '../templates/knowledge/DeepOceanTemplate';
import { ClassicalMechanicsTemplate } from '../templates/knowledge/ClassicalMechanicsTemplate';
import { ProductDesignTemplate } from '../templates/knowledge/ProductDesignTemplate';
import { RelativityTemplate } from '../templates/knowledge/RelativityTemplate';
import { FirstPrinciplesTemplate } from '../templates/knowledge/FirstPrinciplesTemplate';
import { MaslowNeedsTemplate } from '../templates/knowledge/MaslowNeedsTemplate';
import { StatisticsTemplate } from '../templates/knowledge/StatisticsTemplate';
import { LinearAlgebraTemplate } from '../templates/knowledge/LinearAlgebraTemplate';
import { OrganicChemTemplate } from '../templates/knowledge/OrganicChemTemplate';
import { EvolutionBioTemplate } from '../templates/knowledge/EvolutionBioTemplate';
import { PublicSpeakingTemplate } from '../templates/knowledge/PublicSpeakingTemplate';
import { ProjectMgmtTemplate } from '../templates/knowledge/ProjectMgmtTemplate';
import { WoodworkingCraftTemplate } from '../templates/knowledge/WoodworkingCraftTemplate';
import { VideoEditingTemplate } from '../templates/knowledge/VideoEditingTemplate';
import { VaccineImmuneTemplate } from '../templates/knowledge/VaccineImmuneTemplate';
import { BlackHoleTemplate } from '../templates/knowledge/BlackHoleTemplate';
import { BrainPlasticityTemplate } from '../templates/knowledge/BrainPlasticityTemplate';
import { SuperconductorTemplate } from '../templates/knowledge/SuperconductorTemplate';
import { PersonalFinanceTemplate } from '../templates/knowledge/PersonalFinanceTemplate';
import { HousePlantTemplate } from '../templates/knowledge/HousePlantTemplate';
import { SurvivalSkillsTemplate } from '../templates/knowledge/SurvivalSkillsTemplate';
import { PetCareTemplate } from '../templates/knowledge/PetCareTemplate';
import { CriticalThinkingTemplate } from '../templates/knowledge/CriticalThinkingTemplate';
import { HabitFormationTemplate } from '../templates/knowledge/HabitFormationTemplate';
import { MemoryTechTemplate } from '../templates/knowledge/MemoryTechTemplate';
import { PositivePsychTemplate } from '../templates/knowledge/PositivePsychTemplate';
import { ColorPsychTemplate } from '../templates/knowledge/ColorPsychTemplate';
import { NeuroScienceLectureTemplate } from '../templates/knowledge/NeuroScienceLectureTemplate';
import { DatabaseIndexExpertTemplate } from '../templates/knowledge/DatabaseIndexExpertTemplate';
import { CosmicScaleJourneyTemplate } from '../templates/knowledge/CosmicScaleJourneyTemplate';
import { EmergencyFirstAidTemplate } from '../templates/knowledge/EmergencyFirstAidTemplate';
import { CompoundEffectMagicTemplate } from '../templates/knowledge/CompoundEffectMagicTemplate';

export const KnowledgeTemplatesCompositions: React.FC = () => {
  return (
    <>
      {/* ==================== 1. 基础学科与学术教育 ==================== */}
      <Composition
        id="KnowledgeAcademicEducation"
        component={AcademicEducationTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          subject: '物理',
          subjectIcon: '⚛️',
          topic: '能量守恒定律',
          subtitle: 'Law of Conservation of Energy',
          points: [
            { title: '定义', detail: '能量既不会凭空产生，也不会凭空消灭，只能从一种形式转化为另一种形式', icon: '📖' },
            { title: '动能', detail: 'Ek = ½mv²，与物体质量和速度的平方成正比', icon: '🏃' },
            { title: '势能', detail: 'Ep = mgh，与物体质量、重力加速度和高度成正比', icon: '⛰️' },
            { title: '转化', detail: '动能和势能可以相互转化，总机械能保持不变', icon: '🔄' },
          ],
          formula: 'Ek₁ + Ep₁ = Ek₂ + Ep₂',
          formulaExplain: '初状态总能量 = 末状态总能量',
          summary: '能量守恒是物理学最基本的定律之一',
          accentColor: '#667eea',
          accentColorLight: 'rgba(102, 126, 234, 0.15)',
        }}
      />

      {/* 学术教育 - 数学变体 */}
      <Composition
        id="KnowledgeAcademicMath"
        component={AcademicEducationTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          subject: '数学',
          subjectIcon: '📐',
          topic: '勾股定理',
          subtitle: 'Pythagorean Theorem',
          points: [
            { title: '定理', detail: '直角三角形中，两条直角边的平方和等于斜边的平方', icon: '📖' },
            { title: '公式', detail: 'a² + b² = c²，其中 c 为斜边', icon: '✏️' },
            { title: '逆定理', detail: '若三角形三边满足 a² + b² = c²，则该三角形为直角三角形', icon: '🔄' },
            { title: '应用', detail: '测量距离、建筑设计、导航定位等', icon: '🏗️' },
          ],
          formula: 'a² + b² = c²',
          formulaExplain: '直角边² + 直角边² = 斜边²',
          summary: '勾股定理是几何学的基石',
          accentColor: '#06b6d4',
          accentColorLight: 'rgba(6, 182, 212, 0.15)',
        }}
      />

      {/* ==================== 2. 职业技能与实操技术 ==================== */}
      <Composition
        id="KnowledgeProfessionalSkills"
        component={ProfessionalSkillsTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          category: '设计技能',
          categoryIcon: '🎨',
          skillName: 'Figma 组件化设计',
          difficulty: '进阶',
          steps: [
            { step: '01', title: '创建基础组件', detail: '使用 Auto Layout 构建可复用的原子组件', icon: '🧩' },
            { step: '02', title: '定义变体', detail: '通过 Variants 管理组件的不同状态和尺寸', icon: '🔀' },
            { step: '03', title: '构建组件库', detail: '将组件发布到团队共享库，统一设计语言', icon: '📚' },
            { step: '04', title: '实战应用', detail: '在实际项目中灵活调用组件，快速搭建页面', icon: '🚀' },
          ],
          keyTip: '组件命名要规范，建议使用 "类别/名称/状态" 格式',
          toolsUsed: ['Figma', 'FigJam', 'Tokens Studio'],
        }}
      />

      {/* 职业技能 - 编程变体 */}
      <Composition
        id="KnowledgeProfessionalCoding"
        component={ProfessionalSkillsTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          category: '编程开发',
          categoryIcon: '💻',
          skillName: 'Git 团队协作工作流',
          difficulty: '入门',
          steps: [
            { step: '01', title: '创建分支', detail: '从 main 分支创建功能分支 feature/xxx', icon: '🌿' },
            { step: '02', title: '提交代码', detail: '小步提交，写清晰的 commit message', icon: '📝' },
            { step: '03', title: '发起 PR', detail: '推送到远程，创建 Pull Request 等待 Review', icon: '🔀' },
            { step: '04', title: '合并上线', detail: 'Review 通过后合并到 main，触发 CI/CD', icon: '🚀' },
          ],
          keyTip: '每个 PR 聚焦一个功能，方便 Review 和回滚',
          toolsUsed: ['Git', 'GitHub', 'VS Code'],
        }}
      />

      {/* ==================== 3. 硬核科普与通识教育 ==================== */}
      <Composition
        id="KnowledgeSciencePopularization"
        component={SciencePopularizationTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          hookQuestion: '你知道吗？',
          topic: '人类大脑的惊人算力',
          subtitle: 'The Computing Power of Human Brain',
          bigNumber: {
            value: 1000000000000000,
            unit: '次/秒',
            description: '大脑每秒可进行的突触运算次数',
          },
          dataPoints: [
            { label: '神经元', value: 860, unit: '亿个', color: '#f472b6' },
            { label: '突触连接', value: 100, unit: '万亿', color: '#818cf8' },
            { label: '信号速度', value: 120, unit: 'm/s', color: '#34d399' },
            { label: '能耗', value: 20, unit: '瓦特', color: '#fbbf24' },
          ],
          coreExplanation: '人脑仅用 20 瓦能耗（相当于一个灯泡），就实现了超级计算机数千万瓦才能达到的计算效率',
          comparison: {
            left: { label: '人脑', icon: '🧠', value: '20W' },
            right: { label: '超算', icon: '🖥️', value: '20MW' },
            vs: '效率差 100 万倍',
          },
          source: 'Nature Neuroscience, 2024',
        }}
      />

      {/* ==================== 4. 泛知识与生活百科 ==================== */}
      <Composition
        id="KnowledgeLifeKnowledge"
        component={LifeKnowledgeTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          topicIcon: '🥗',
          topic: '健康饮食的 5 个误区',
          tagline: '你可能每天都在犯的错误',
          tips: [
            { icon: '❌', title: '不吃碳水就能瘦', detail: '碳水是大脑主要能量来源，长期缺乏会导致注意力下降、代谢降低' },
            { icon: '❌', title: '果汁等于水果', detail: '榨汁后膳食纤维大量流失，糖分浓缩，血糖上升速度是吃水果的 3 倍' },
            { icon: '❌', title: '蛋黄胆固醇高不能吃', detail: '最新研究表明，每天 1-2 个鸡蛋不会增加心血管风险' },
            { icon: '❌', title: '晚上 6 点后不能吃东西', detail: '关键是总热量摄入，而非进食时间；适量晚餐反而有利于睡眠' },
            { icon: '❌', title: '喝骨头汤补钙', detail: '骨头汤含钙量极低（仅 2mg/100ml），更多的是脂肪和嘌呤' },
          ],
          closingText: '科学饮食，拒绝跟风 🧪',
          themeColor: '#f97316',
        }}
      />

      {/* 生活百科 - 睡眠变体 */}
      <Composition
        id="KnowledgeLifeSleep"
        component={LifeKnowledgeTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          topicIcon: '😴',
          topic: '提升睡眠质量的 5 个方法',
          tagline: '今晚就能用上的实用技巧',
          tips: [
            { icon: '🌡️', title: '控制室温 18-22°C', detail: '核心体温下降有助于入睡，凉爽的环境是最佳睡眠条件' },
            { icon: '📱', title: '睡前 1 小时远离蓝光', detail: '蓝光抑制褪黑素分泌，影响昼夜节律' },
            { icon: '☕', title: '下午 2 点后不摄入咖啡因', detail: '咖啡因半衰期为 5-6 小时，下午饮用会严重影响入睡' },
            { icon: '🧘', title: '建立固定睡前仪式', detail: '冥想、阅读、拉伸等，让大脑形成"该睡觉了"的条件反射' },
            { icon: '⏰', title: '固定起床时间', detail: '即使周末也保持一致，稳定生物钟是高质量睡眠的关键' },
          ],
          closingText: '好睡眠，好状态 🌙',
          themeColor: '#6366f1',
        }}
      />

      {/* ==================== 5. 思维启发与个人成长 ==================== */}
      <Composition
        id="KnowledgeMindsetGrowth"
        component={MindsetGrowthTemplate}
        durationInFrames={450}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          category: '认知升级',
          quote: '你的认知边界，就是你的世界边界。突破认知，才能突破人生。',
          author: '查理·芒格',
          authorTitle: '伯克希尔·哈撒韦副董事长',
          insights: [
            { icon: '🔑', text: '多元思维模型：用不同学科的视角看同一个问题' },
            { icon: '🪞', text: '反向思考：想知道如何成功，先研究如何失败' },
            { icon: '📚', text: '终身学习：每天睡前都要比醒来时聪明一点' },
          ],
          reflection: '今天，你突破了哪一个思维边界？',
          accentGradient: ['#c084fc', '#818cf8'],
        }}
      />

      {/* 思维启发 - 第一性原理变体 */}
      <Composition
        id="KnowledgeMindsetFirstPrinciples"
        component={MindsetGrowthTemplate}
        durationInFrames={450}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          category: '第一性原理',
          quote: '不要用类比来推理，要从最基本的真理出发，逐步推导出结论。',
          author: '埃隆·马斯克',
          authorTitle: 'SpaceX & Tesla CEO',
          insights: [
            { icon: '🔬', text: '分解问题：把复杂问题拆到不可再分的基本事实' },
            { icon: '🧱', text: '重新构建：从基本事实出发，重新推导解决方案' },
            { icon: '🚀', text: '突破常规：挑战 "一直以来都是这样做的" 的思维' },
          ],
          reflection: '你的哪个假设，其实从未被验证过？',
          accentGradient: ['#f97316', '#ef4444'],
        }}
      />

      {/* ==================== 6. 心理学 ==================== */}
      <Composition
        id="KnowledgePsychology"
        component={PsychologyTemplate}
        durationInFrames={660}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          conceptName: '达克效应',
          conceptNameEn: 'Dunning-Kruger Effect',
          definition: '能力欠缺的人往往高估自己的能力，而真正有能力的人反而倾向于低估自己',
          stages: [
            { label: '愚昧之巅', description: '初学者的盲目自信', icon: '⛰️' },
            { label: '绝望之谷', description: '发现自己的无知', icon: '🕳️' },
            { label: '开悟之坡', description: '持续学习，稳步上升', icon: '📈' },
            { label: '持续平稳', description: '真正的专家谦逊', icon: '🏔️' },
          ],
          examples: [
            { scenario: '刚学编程一周就觉得自己能开发 App', icon: '💻' },
            { scenario: '读了一篇科普就认为自己比专家更懂', icon: '📰' },
            { scenario: '新手投资者连续盈利后觉得自己是股神', icon: '📊' },
          ],
          takeaway: '真正的智慧，始于承认自己的无知',
          selfReflection: '你目前在哪个阶段？',
          themeColors: { primary: '#14b8a6', secondary: '#a78bfa' },
        }}
      />

      {/* 心理学 - 锚定效应变体 */}
      <Composition
        id="KnowledgePsychologyAnchoring"
        component={PsychologyTemplate}
        durationInFrames={660}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          conceptName: '锚定效应',
          conceptNameEn: 'Anchoring Effect',
          definition: '人们在做决策时，会过度依赖最先接收到的信息（"锚"），即使这个信息与决策无关',
          stages: [
            { label: '接收锚点', description: '最先获取的信息', icon: '⚓' },
            { label: '形成偏差', description: '围绕锚点调整判断', icon: '🎯' },
            { label: '做出决策', description: '偏向锚点的结论', icon: '✅' },
            { label: '事后合理化', description: '为偏差找理由', icon: '🧩' },
          ],
          examples: [
            { scenario: '商品标原价 999、现价 299，你觉得很便宜', icon: '🏷️' },
            { scenario: '面试时先说期望薪资 30K，最终谈到 25K', icon: '💰' },
            { scenario: '房产中介先带你看最贵的房子', icon: '🏠' },
          ],
          takeaway: '觉察你的 "锚" 在哪里，才能做出理性决策',
          selfReflection: '上一次被 "锚" 影响是什么时候？',
          themeColors: { primary: '#f59e0b', secondary: '#ef4444' },
        }}
      />

      {/* ============================================================ */}
      {/* 第二批模板：风格变体 + 横竖屏适配                               */}
      {/* ============================================================ */}

      {/* ==================== 1B. 基础学科 - 横屏「黑板」风格 ==================== */}
      <Composition
        id="KnowledgeChalkboardMath"
        component={AcademicChalkboardTemplate}
        durationInFrames={500}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          subject: '高中数学',
          topic: '等差数列求和公式推导',
          subtitle: 'Sum of Arithmetic Sequence',
          steps: [
            { title: '定义', content: '首项 a₁，公差 d，第 n 项 aₙ = a₁ + (n-1)d' },
            { title: '正序求和', content: 'Sₙ = a₁ + a₂ + a₃ + ... + aₙ' },
            { title: '倒序求和', content: 'Sₙ = aₙ + aₙ₋₁ + ... + a₁' },
            { title: '两式相加', content: '2Sₙ = n(a₁ + aₙ)' },
          ],
          keyFormula: 'Sₙ = n(a₁ + aₙ) / 2',
          conclusion: '倒序相加法是求等差数列和的经典方法',
        }}
      />

      <Composition
        id="KnowledgeChalkboardPhysics"
        component={AcademicChalkboardTemplate}
        durationInFrames={500}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          subject: '高中物理',
          topic: '万有引力定律',
          subtitle: 'Law of Universal Gravitation',
          steps: [
            { title: '观察', content: '苹果下落、月球绕地 → 同一种力？' },
            { title: '猜想', content: '引力与质量成正比，与距离平方成反比' },
            { title: '验证', content: '月球轨道加速度 = g/3600，恰好 = R²/r²' },
            { title: '公式', content: 'F = GMm/r²，G = 6.674×10⁻¹¹ N·m²/kg²' },
          ],
          keyFormula: 'F = GMm / r²',
          conclusion: '牛顿用同一个公式统一了天上和地上的力学',
          chalkColor: '#e0d8c8',
        }}
      />

      {/* ==================== 2B. 职业技能 - 横屏「仪表盘」风格 ==================== */}
      <Composition
        id="KnowledgeDashboardExcel"
        component={SkillsDashboardTemplate}
        durationInFrames={500}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: 'Excel 数据透视表',
          subtitle: '10 分钟掌握核心操作',
          steps: [
            { title: '选择数据源', detail: '确保数据有表头，无空行空列', icon: '📋', duration: '1 min' },
            { title: '插入透视表', detail: '插入 → 数据透视表 → 选择位置', icon: '📊', duration: '2 min' },
            { title: '拖拽字段', detail: '将维度拖到行/列，指标拖到值区域', icon: '🔀', duration: '3 min' },
            { title: '设置汇总方式', detail: '右键值字段 → 值字段设置 → 求和/计数/平均', icon: '⚙️', duration: '2 min' },
            { title: '添加筛选器', detail: '将筛选维度拖到筛选区或使用切片器', icon: '🔍', duration: '2 min' },
          ],
          metrics: [
            { label: '效率提升', value: 85, maxValue: 100, color: '#10b981' },
            { label: '数据洞察', value: 90, maxValue: 100, color: '#6366f1' },
            { label: '上手难度', value: 30, maxValue: 100, color: '#f59e0b' },
          ],
          keyOutput: '从原始数据到可视化报表，5 步搞定',
          tags: ['Excel', '数据分析', '办公技能', '效率工具'],
        }}
      />

      <Composition
        id="KnowledgeDashboardPrompt"
        component={SkillsDashboardTemplate}
        durationInFrames={500}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: 'AI Prompt Engineering',
          subtitle: '让 AI 输出质量翻倍的技巧',
          steps: [
            { title: '明确角色', detail: '告诉 AI 它是谁：你是一位资深前端工程师', icon: '🎭', duration: '30s' },
            { title: '给出上下文', detail: '描述背景、约束条件、目标受众', icon: '📋', duration: '1 min' },
            { title: '结构化指令', detail: '使用 Markdown 格式、分步骤、给示例', icon: '📐', duration: '2 min' },
            { title: '指定输出格式', detail: '要求 JSON/表格/代码块等特定格式', icon: '📄', duration: '1 min' },
            { title: '迭代优化', detail: '基于输出反馈，逐步细化 Prompt', icon: '🔄', duration: '2 min' },
          ],
          metrics: [
            { label: '输出质量', value: 92, maxValue: 100, color: '#10b981' },
            { label: '效率提升', value: 78, maxValue: 100, color: '#6366f1' },
            { label: '学习曲线', value: 25, maxValue: 100, color: '#f59e0b' },
          ],
          keyOutput: '好的 Prompt = 角色 + 上下文 + 指令 + 格式 + 示例',
          tags: ['AI', 'ChatGPT', 'Prompt', '效率'],
        }}
      />

      {/* ==================== 3B. 硬核科普 - 横屏「信息图表」风格 ==================== */}
      <Composition
        id="KnowledgeInfographicClimate"
        component={ScienceInfographicTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          topic: '全球变暖：数据背后的真相',
          hookStat: { number: '1.1°C', label: '全球平均温度已比工业化前上升' },
          description: '过去 170 年的数据揭示了一个加速变暖的趋势，而最近 50 年的变化尤为剧烈',
          barChart: {
            title: '近五十年全球温度变化（相对于 1951-1980 基准）',
            data: [
              { label: '1980', value: 26, color: '#fbbf24' },
              { label: '1990', value: 43, color: '#f97316' },
              { label: '2000', value: 58, color: '#ef4444' },
              { label: '2010', value: 72, color: '#dc2626' },
              { label: '2020', value: 98, color: '#b91c1c' },
            ],
            unit: '温度异常 (×0.01°C)',
          },
          timeline: {
            title: '关键里程碑',
            events: [
              { year: '1988', event: 'IPCC 成立' },
              { year: '1997', event: '京都议定书' },
              { year: '2015', event: '巴黎协定' },
              { year: '2023', event: '史上最热年' },
            ],
          },
          factCards: [
            { icon: '🌊', title: '海平面上升', detail: '过去 100 年上升约 20cm' },
            { icon: '🧊', title: '冰川消融', detail: '北极冰量每十年减少 13%' },
            { icon: '🌪️', title: '极端天气', detail: '强台风频率增加 25%' },
          ],
          source: 'IPCC AR6 报告 | NASA GISS 数据集',
        }}
      />

      <Composition
        id="KnowledgeInfographicSleep"
        component={ScienceInfographicTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          topic: '睡眠科学：你真的会睡觉吗？',
          hookStat: { number: '36%', label: '中国成年人存在睡眠障碍' },
          description: '睡眠不仅是休息，更是大脑进行记忆巩固、毒素清除和激素调节的关键时期',
          barChart: {
            title: '不同睡眠时长对认知能力的影响（相对于 8h 基准）',
            data: [
              { label: '4h', value: 35, color: '#dc2626' },
              { label: '5h', value: 55, color: '#f97316' },
              { label: '6h', value: 72, color: '#fbbf24' },
              { label: '7h', value: 90, color: '#84cc16' },
              { label: '8h', value: 100, color: '#16a34a' },
            ],
            unit: '认知能力 (%)',
          },
          timeline: {
            title: '一个睡眠周期（90分钟）',
            events: [
              { year: 'N1', event: '浅睡眠' },
              { year: 'N2', event: '中度睡眠' },
              { year: 'N3', event: '深度睡眠' },
              { year: 'REM', event: '快速眼动' },
            ],
          },
          factCards: [
            { icon: '🧹', title: '大脑清洁', detail: '深睡时清除β-淀粉样蛋白' },
            { icon: '💾', title: '记忆巩固', detail: 'REM 阶段整合白天的学习' },
            { icon: '⚖️', title: '激素平衡', detail: '调节皮质醇、生长激素' },
          ],
          source: 'Nature Reviews Neuroscience, 2024',
        }}
      />

      {/* ==================== 4B. 泛知识 - 竖屏「杂志」风格 ==================== */}
      <Composition
        id="KnowledgeMagazineImmunity"
        component={LifeMagazineTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          category: 'HEALTH & WELLNESS',
          headline: '提升免疫力\n最有效的 5 件事',
          subheadline: '来自哈佛医学院的建议',
          items: [
            { rank: 1, title: '充足睡眠', subtitle: '每晚 7-9 小时', highlight: '#1', highlightColor: '#dc2626' },
            { rank: 2, title: '规律运动', subtitle: '每周 150 分钟中等强度', highlight: '#2', highlightColor: '#ea580c' },
            { rank: 3, title: '均衡饮食', subtitle: '多蔬果、全谷物、优质蛋白', highlight: '#3', highlightColor: '#d97706' },
            { rank: 4, title: '压力管理', subtitle: '冥想、深呼吸、社交支持', highlight: '#4', highlightColor: '#65a30d' },
            { rank: 5, title: '戒烟限酒', subtitle: '烟酒直接损害免疫细胞', highlight: '#5', highlightColor: '#0891b2' },
          ],
          editorNote: '以上方法简单却被大多数人忽略',
          bgColor: '#1c1917',
          accentColor: '#dc2626',
        }}
      />

      <Composition
        id="KnowledgeMagazineProductivity"
        component={LifeMagazineTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          category: 'PRODUCTIVITY',
          headline: '高效人士的\n5 个时间管理法则',
          subheadline: '从混乱到掌控的实战策略',
          items: [
            { rank: 1, title: '两分钟法则', subtitle: '能在 2 分钟内完成的事立刻做', highlight: '#1', highlightColor: '#6366f1' },
            { rank: 2, title: '时间块划分', subtitle: '将一天分为 90 分钟的专注块', highlight: '#2', highlightColor: '#8b5cf6' },
            { rank: 3, title: '帕累托原则', subtitle: '找到 20% 的关键任务优先完成', highlight: '#3', highlightColor: '#a855f7' },
            { rank: 4, title: '番茄工作法', subtitle: '25 分钟专注 + 5 分钟休息', highlight: '#4', highlightColor: '#c084fc' },
            { rank: 5, title: '每日回顾', subtitle: '睡前 10 分钟复盘今天、规划明天', highlight: '#5', highlightColor: '#d8b4fe' },
          ],
          editorNote: '时间管理的核心不是做更多，而是做更重要的事',
          bgColor: '#0f172a',
          accentColor: '#6366f1',
        }}
      />

      {/* ==================== 5B. 思维启发 - 竖屏「电影感」风格 ==================== */}
      <Composition
        id="KnowledgeCinematicGates"
        component={MindsetCinematicTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          quote: '大多数人高估了一年能做的事，却低估了十年能做的事。',
          author: '比尔·盖茨',
          authorTitle: 'Bill Gates',
          coreIdea: '长期主义：放弃短期回报的诱惑，把时间投入到真正重要的事情上',
          actionItems: [
            '选择一个值得用十年去做的事',
            '每天进步 1%，一年后就是 37 倍',
            '记录过程，而不仅仅追求结果',
          ],
          endingLine: '你的十年之约，从今天开始。',
          moodColor: '#c084fc',
        }}
      />

      <Composition
        id="KnowledgeCinematicJobs"
        component={MindsetCinematicTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          quote: '你无法在展望未来时串联起生命中的点滴，只能在回顾过去时才能连点成线。',
          author: '史蒂夫·乔布斯',
          authorTitle: 'Steve Jobs · Stanford Commencement, 2005',
          coreIdea: '信任过程：每一段看似无关的经历，都在为未来的某个节点做准备',
          actionItems: [
            '不要因为看不到全局而放弃当下的努力',
            '保持好奇心，跨领域探索',
            '相信一切终将有意义',
          ],
          endingLine: 'Stay hungry, stay foolish.',
          moodColor: '#38bdf8',
        }}
      />

      {/* ==================== 6B. 心理学 - 横屏「实验室」风格 ==================== */}
      <Composition
        id="KnowledgeLabMarshmallow"
        component={PsychologyLabTemplate}
        durationInFrames={510}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          experimentName: '棉花糖实验',
          researcher: 'Walter Mischel',
          year: '1972',
          hypothesis: '能够延迟满足的儿童，未来的学业和社会表现会更好',
          phases: [
            { phase: '准备', description: '给儿童一块棉花糖，告知等 15 分钟可获得两块', icon: '🍬' },
            { phase: '观察', description: '研究者离开房间，隐藏摄像头记录儿童行为', icon: '📹' },
            { phase: '结果', description: '约 1/3 儿童成功等待，2/3 提前吃掉', icon: '📊' },
            { phase: '追踪', description: '数十年后追踪发现：延迟满足者 SAT 平均高 210 分', icon: '📈' },
          ],
          result: '延迟满足能力与自控力、学业成就、健康水平显著正相关',
          stats: [
            { value: '600+', label: '参与儿童', color: '#6366f1' },
            { value: '40年', label: '追踪时长', color: '#0891b2' },
            { value: '210分', label: 'SAT 差距', color: '#dc2626' },
            { value: '1/3', label: '成功延迟', color: '#16a34a' },
          ],
          insight: '自控力不是天赋，而是可以训练的技能——关键在于转移注意力策略',
          applicationTip: '面对诱惑时，尝试把注意力从"奖励本身"转移到"等待的策略"上',
        }}
      />

      <Composition
        id="KnowledgeLabMilgram"
        component={PsychologyLabTemplate}
        durationInFrames={510}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          experimentName: '米尔格拉姆服从实验',
          researcher: 'Stanley Milgram',
          year: '1963',
          hypothesis: '在权威指令下，普通人会对他人施加伤害',
          phases: [
            { phase: '设置', description: '被试扮演"教师"，对"学生"（演员）施加电击', icon: '⚡' },
            { phase: '升级', description: '每次回答错误，电压增加 15V，直到 450V', icon: '📈' },
            { phase: '施压', description: '实验者用四级指令要求继续：请继续 → 你必须继续', icon: '🗣️' },
            { phase: '发现', description: '65% 的被试执行到了最高 450V 电击', icon: '😱' },
          ],
          result: '在权威情境下，大多数普通人会做出违背良心的行为',
          stats: [
            { value: '65%', label: '完全服从', color: '#dc2626' },
            { value: '450V', label: '最高电压', color: '#f59e0b' },
            { value: '40人', label: '首批被试', color: '#6366f1' },
            { value: '18次', label: '重复验证', color: '#0891b2' },
          ],
          insight: '恶行不一定来自恶人——情境的力量远超我们的想象',
          applicationTip: '当"权威"要求你做违背良心的事时，停下来问自己：如果没有这个指令，我会这么做吗？',
        }}
      />

      {/* ============================================================ */}
      {/* 第三批模板：更多风格变体                                        */}
      {/* ============================================================ */}

      {/* ==================== 1C. 基础学科 - 横屏「现代课件」风格 ==================== */}
      <Composition
        id="KnowledgeSlideBiology"
        component={AcademicSlideTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          pageNumber: '03',
          subject: '生物学 · 细胞',
          title: '有丝分裂的四个时期',
          definition: '有丝分裂是真核细胞分裂的主要方式，确保遗传信息精确复制并均等分配',
          sections: [
            { label: '前期', content: '染色质→染色体，核膜解体，纺锤体形成', visual: '🧬' },
            { label: '中期', content: '染色体排列在赤道板上，纺锤丝与着丝点连接', visual: '📍' },
            { label: '后期', content: '着丝点分裂，姐妹染色单体分开移向两极', visual: '↔️' },
            { label: '末期', content: '核膜重建，染色体→染色质，细胞板形成', visual: '🔬' },
          ],
          keyVisual: { emoji: '🧫', caption: '一次有丝分裂 ≈ 1-2 小时' },
          bottomNote: '有丝分裂保证了子细胞与母细胞遗传组成相同',
          accentColor: '#0d9488',
        }}
      />

      <Composition
        id="KnowledgeSlideChemistry"
        component={AcademicSlideTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          pageNumber: '07',
          subject: '化学 · 反应',
          title: '化学平衡移动原理',
          definition: '勒夏特列原理：改变平衡体系的条件，平衡会向减弱这种改变的方向移动',
          sections: [
            { label: '温度', content: '升温 → 平衡向吸热方向移动', visual: '🌡️' },
            { label: '浓度', content: '增大反应物浓度 → 平衡正向移动', visual: '🧪' },
            { label: '压强', content: '增大压强 → 平衡向体积减小方向移动', visual: '🫧' },
            { label: '催化剂', content: '同等加速正逆反应，平衡不移动', visual: '⚡' },
          ],
          keyVisual: { emoji: '⚖️', caption: '平衡是动态的，不是静止的' },
          bottomNote: '催化剂只改变达到平衡的速率，不改变平衡位置',
          accentColor: '#7c3aed',
        }}
      />

      {/* ==================== 2C. 职业技能 - 竖屏「任务清单」风格 ==================== */}
      <Composition
        id="KnowledgeChecklistFrontend"
        component={SkillsChecklistTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          listTitle: '前端面试准备清单',
          listIcon: '📋',
          description: '系统复习路线，两周搞定高频考点',
          items: [
            { text: 'JavaScript 原型链与闭包', tag: '核心', tagColor: '#ef4444' },
            { text: 'CSS Flex/Grid 布局精通', tag: '基础', tagColor: '#3b82f6' },
            { text: 'React Hooks 原理与最佳实践', tag: '框架', tagColor: '#8b5cf6' },
            { text: 'HTTP 协议与浏览器渲染机制', tag: '网络', tagColor: '#f59e0b' },
            { text: '手写 Promise / 防抖节流', tag: '手撕', tagColor: '#ef4444' },
            { text: 'Webpack 构建优化', tag: '工程', tagColor: '#06b6d4' },
            { text: 'TypeScript 泛型与类型体操', tag: '进阶', tagColor: '#8b5cf6' },
            { text: '算法：排序 / 二叉树 / 动态规划', tag: '算法', tagColor: '#10b981' },
          ],
          completionText: '全部完成，自信出击！',
          proTip: '每天集中攻克 1-2 个专题，比零散刷题更有效',
        }}
      />

      <Composition
        id="KnowledgeChecklistDesign"
        component={SkillsChecklistTemplate}
        durationInFrames={440}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          listTitle: 'UI 设计入门路线',
          listIcon: '🎨',
          description: '从零到能独立出图的学习清单',
          items: [
            { text: '色彩理论与配色原则', tag: '基础', tagColor: '#3b82f6' },
            { text: '字体搭配与排版规则', tag: '基础', tagColor: '#3b82f6' },
            { text: 'Figma 核心操作与快捷键', tag: '工具', tagColor: '#8b5cf6' },
            { text: '组件化设计思维', tag: '核心', tagColor: '#ef4444' },
            { text: 'iOS & Material Design 规范', tag: '规范', tagColor: '#06b6d4' },
            { text: '交互动效基础（Protopie）', tag: '进阶', tagColor: '#f59e0b' },
          ],
          completionText: '恭喜入门！开始实战吧',
          proTip: '临摹优秀作品是最快的学习方式，每周至少临摹 2 个',
        }}
      />

      {/* ==================== 4C. 泛知识 - 横屏「对比分析」风格 ==================== */}
      <Composition
        id="KnowledgeComparisonSleep"
        component={LifeComparisonTemplate}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          topic: '早起 vs 晚睡：哪个更高效？',
          leftLabel: '早起型',
          leftIcon: '🌅',
          leftColor: '#f59e0b',
          rightLabel: '晚睡型',
          rightIcon: '🌙',
          rightColor: '#6366f1',
          rows: [
            { dimension: '专注力峰值', left: '上午 9-11 点', right: '晚上 9-12 点', winner: 'tie' },
            { dimension: '身体激素', left: '皮质醇自然升高，精力充沛', right: '褪黑素延迟，需要额外刺激', winner: 'left' },
            { dimension: '社交适配', left: '与社会节奏一致', right: '容易错过上午安排', winner: 'left' },
            { dimension: '创造力', left: '适合逻辑分析类工作', right: '适合发散性创意工作', winner: 'tie' },
            { dimension: '长期健康', left: '与昼夜节律一致，睡眠质量更高', right: '易导致慢性睡眠不足', winner: 'left' },
          ],
          verdict: '早起型整体略优',
          verdictDetail: '但最重要的是：保持固定的作息节奏，比早晚本身更关键',
        }}
      />

      <Composition
        id="KnowledgeComparisonCoffee"
        component={LifeComparisonTemplate}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          topic: '咖啡 vs 茶：谁是更好的提神饮品？',
          leftLabel: '咖啡',
          leftIcon: '☕',
          leftColor: '#92400e',
          rightLabel: '茶',
          rightIcon: '🍵',
          rightColor: '#15803d',
          rows: [
            { dimension: '咖啡因含量', left: '95-200mg/杯', right: '25-50mg/杯', winner: 'left' },
            { dimension: '提神速度', left: '15-30 分钟起效', right: '30-60 分钟缓释', winner: 'tie' },
            { dimension: '持续时间', left: '3-5 小时，可能有崩溃感', right: '4-6 小时，平缓持久', winner: 'right' },
            { dimension: '抗氧化物', left: '绿原酸，保护心血管', right: '茶多酚 + 儿茶素，抗癌', winner: 'tie' },
            { dimension: '睡眠影响', left: '下午饮用影响较大', right: '影响相对温和', winner: 'right' },
          ],
          verdict: '各有千秋',
          verdictDetail: '早晨提神选咖啡，下午提神选绿茶，关键是别超过每天 400mg 咖啡因',
        }}
      />

      {/* ==================== 5C. 思维启发 - 横屏「思维导图」风格 ==================== */}
      <Composition
        id="KnowledgeMindmapDeepWork"
        component={MindsetMindmapTemplate}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          centralTopic: '深度工作',
          centralIcon: '🧠',
          subtitle: 'Deep Work — Cal Newport',
          branches: [
            { label: '消除干扰', icon: '🔕', detail: '关闭通知、设定"免打扰"时段', color: '#ef4444' },
            { label: '时间块划分', icon: '⏰', detail: '90分钟专注块 + 20分钟休息', color: '#f59e0b' },
            { label: '刻意练习', icon: '🎯', detail: '在舒适区边缘挑战自我', color: '#10b981' },
            { label: '仪式感', icon: '☕', detail: '固定地点、固定流程启动深度状态', color: '#6366f1' },
            { label: '量化产出', icon: '📊', detail: '追踪深度工作小时数而非工作时长', color: '#ec4899' },
            { label: '恢复能力', icon: '🌿', detail: '白天不做完的事别占用晚间大脑', color: '#0891b2' },
          ],
          insight: '在注意力稀缺的时代，深度工作是最有价值的超能力',
        }}
      />

      <Composition
        id="KnowledgeMindmapSecondBrain"
        component={MindsetMindmapTemplate}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          centralTopic: '第二大脑',
          centralIcon: '💾',
          subtitle: 'Building a Second Brain — Tiago Forte',
          branches: [
            { label: '捕获', icon: '📥', detail: '随时记录灵感、笔记、想法', color: '#3b82f6' },
            { label: '组织', icon: '📂', detail: 'PARA 系统：项目/领域/资源/归档', color: '#8b5cf6' },
            { label: '提炼', icon: '✨', detail: '渐进式总结，提取核心观点', color: '#f59e0b' },
            { label: '表达', icon: '🎤', detail: '将知识转化为创作和分享', color: '#ef4444' },
            { label: '连接', icon: '🔗', detail: '建立知识之间的关联网络', color: '#10b981' },
            { label: '复用', icon: '♻️', detail: '中间产物可反复利用，降低创作成本', color: '#ec4899' },
          ],
          insight: '你的笔记不是知识的坟墓，而是创造力的弹药库',
        }}
      />

      {/* ==================== 6C. 心理学 - 竖屏「案例故事」风格 ==================== */}
      <Composition
        id="KnowledgeStoryComparison"
        component={PsychologyStoryTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          title: '为什么你总觉得别人过得比你好？',
          titleEn: 'Social Comparison Theory',
          scenes: [
            { emoji: '📱', narration: '你打开朋友圈，看到朋友在马尔代夫度假' },
            { emoji: '😔', narration: '你低头看了看自己加班的工位，突然觉得人生好失败' },
            { emoji: '🧠', narration: '但你不知道的是——他刚被裁员，这是"散心旅行"' },
          ],
          mechanismName: '社会比较理论',
          mechanismExplain: '人们通过与他人比较来评估自己的价值。但社交媒体让我们只看到别人的"精选集"，却拿自己的"幕后花絮"去对比',
          lifeExamples: [
            '刷到别人的年薪百万，忘了他 996 的日常',
            '看到别人秀恩爱，忽略了他们也会吵架',
            '羡慕别人的自律早起，却不知他失眠了',
          ],
          takeaway: '停止用别人的高光时刻，丈量自己的普通日常',
          themeGradient: ['#f472b6', '#a78bfa'],
        }}
      />

      <Composition
        id="KnowledgeStorySunkenCost"
        component={PsychologyStoryTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          title: '你为什么舍不得离开一段烂关系？',
          titleEn: 'Sunk Cost Fallacy',
          scenes: [
            { emoji: '💔', narration: '你和他在一起 3 年了，虽然经常吵架，但就是舍不得分手' },
            { emoji: '🤔', narration: '"我都付出了这么多，现在放手不就亏了吗？"' },
            { emoji: '💡', narration: '但过去的投入已经收不回来了——未来的幸福才是你该关注的' },
          ],
          mechanismName: '沉没成本谬误',
          mechanismExplain: '人们会因为已经投入的时间、金钱或情感成本，而继续坚持一个不理性的决定——即使继续下去的代价更高',
          lifeExamples: [
            '看了一半的烂电影不舍得走',
            '排了很久的队不愿意放弃',
            '亏损的股票一直不肯割肉',
          ],
          takeaway: '已经花掉的钱买不回来，但你还可以决定未来',
          themeGradient: ['#fbbf24', '#f97316'],
        }}
      />

      {/* ===== Batch 4: 辅助动画增强版 ===== */}

      {/* 19. 基础学科 - SVG 动态图解 (横屏) */}
      <Composition
        id="AcademicAnimatedDiagram-wave"
        component={AcademicAnimatedDiagram}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 20. 职业技能 - 代码演示 (横屏) */}
      <Composition
        id="SkillsCodeDemo-debounce"
        component={SkillsCodeDemo}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />
      <Composition
        id="SkillsCodeDemo-promise"
        component={SkillsCodeDemo}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          fileName: 'promise-all.ts',
          language: 'TypeScript',
          title: '手写 Promise.all — 并发控制',
          lines: [
            { code: 'function promiseAll<T>(promises: Promise<T>[]) {', annotation: '接收 Promise 数组' },
            { code: '  return new Promise<T[]>((resolve, reject) => {' },
            { code: '    const results: T[] = [];', annotation: '收集所有结果' },
            { code: '    let count = 0;' },
            { code: '' },
            { code: '    promises.forEach((p, i) => {', isHighlight: true },
            { code: '      p.then(val => {', annotation: '每个 Promise 完成时记录' },
            { code: '        results[i] = val;' },
            { code: '        if (++count === promises.length) {', isHighlight: true },
            { code: '          resolve(results);', annotation: '全部完成才 resolve' },
            { code: '        }' },
            { code: '      }).catch(reject);', annotation: '任一失败立即 reject' },
            { code: '    });' },
            { code: '  });' },
            { code: '}' },
          ],
          outputs: [
            { text: '$ ts-node promise-all.ts', type: 'info' },
            { text: '> 执行 3 个异步任务...', type: 'info' },
            { text: '> [200ms] 任务1完成', type: 'success' },
            { text: '> [350ms] 任务2完成', type: 'success' },
            { text: '> [400ms] 任务3完成', type: 'success' },
            { text: '✅ 全部完成: ["数据A", "数据B", "数据C"]', type: 'result' },
          ],
          summary: 'Promise.all 核心：计数器 + 索引对应结果 + 任一 reject 即终止',
        }}
      />

      {/* 21. 硬核科普 - 数据可视化故事 (竖屏) */}
      <Composition
        id="ScienceDataViz-energy"
        component={ScienceDataViz}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />
      <Composition
        id="ScienceDataViz-ai"
        component={ScienceDataViz}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          topic: 'AI 算力需求指数级增长',
          subtitle: 'AI Compute Growth 2012-2025',
          lineChart: {
            title: 'AI 训练算力需求（PetaFLOP/s-day）',
            data: [
              { x: '2012', y: 0.01 },
              { x: '2015', y: 0.1 },
              { x: '2018', y: 10 },
              { x: '2020', y: 100 },
              { x: '2022', y: 1000 },
              { x: '2025', y: 10000 },
            ],
            unit: 'PF-day',
            color: '#818cf8',
          },
          pieChart: {
            title: 'AI 应用领域分布',
            slices: [
              { label: '自然语言', value: 32, color: '#818cf8' },
              { label: '计算机视觉', value: 26, color: '#34d399' },
              { label: '多模态', value: 24, color: '#fbbf24' },
              { label: '其他', value: 18, color: '#f472b6' },
            ],
          },
          bigStat: { number: 10000, suffix: 'x', description: '过去 10 年 AI 算力增长倍数' },
          insight: 'AI 算力每 3.4 个月翻一番，远超摩尔定律的 2 年翻一番',
          source: 'Epoch AI Compute Trends 2025',
        }}
      />

      {/* 22. 泛知识 - 流程图指南 (竖屏) */}
      <Composition
        id="LifeProcessFlow-morning"
        component={LifeProcessFlow}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 23. 思维启发 - 成长之树 (竖屏) */}
      <Composition
        id="MindsetGrowthTree-personal"
        component={MindsetGrowthTree}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />
      <Composition
        id="MindsetGrowthTree-career"
        component={MindsetGrowthTree}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          title: '职业发展之树',
          subtitle: '深耕能力，收获机遇',
          fruits: [
            { label: '高薪', icon: '💰', color: '#fbbf24' },
            { label: '话语权', icon: '🎤', color: '#818cf8' },
            { label: '自由', icon: '🕊️', color: '#38bdf8' },
            { label: '成就感', icon: '🏆', color: '#f472b6' },
          ],
          roots: [
            { label: '专业技能' },
            { label: '人脉' },
            { label: '行业洞察' },
            { label: '执行力' },
          ],
          trunkLabel: '刻意练习',
          insight: '机遇偏爱有准备的根系',
        }}
      />

      {/* 24. 心理学 - 情绪光谱 (横屏) */}
      <Composition
        id="PsychologySpectrum-tolerance"
        component={PsychologySpectrum}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ===== Batch 5: 扩展专业学科 ===== */}

      {/* 25. 古诗词赏析 - 水墨意境 (竖屏) */}
      <Composition
        id="PoetryInk-jingYeSi"
        component={PoetryInkTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />
      <Composition
        id="PoetryInk-chunXiao"
        component={PoetryInkTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          dynasty: '唐',
          author: '孟浩然',
          title: '春晓',
          lines: [
            { text: '春眠不觉晓', annotatedWords: [{ word: '春眠', note: '春日里的酣睡' }, { word: '不觉晓' }] },
            { text: '处处闻啼鸟', annotatedWords: [{ word: '处处', note: '四面八方' }, { word: '闻啼鸟', highlight: true }] },
            { text: '夜来风雨声', annotatedWords: [{ word: '夜来', note: '昨夜' }, { word: '风雨声' }] },
            { text: '花落知多少', annotatedWords: [{ word: '花落', highlight: true }, { word: '知多少', note: '惜春之情' }] },
          ],
          appreciation: [
            '以「不觉」写春睡之酣，侧面写春天的美好',
            '由听觉入手：鸟啼→风雨→落花，层层联想',
            '「知多少」以问作结，余味无穷',
            '不写看花落，而写猜花落，更含蓄深远',
          ],
          theme: '惜春',
          imagery: ['春眠', '啼鸟', '风雨', '落花'],
          mood: '闲适中的淡淡惋惜',
        }}
      />

      {/* 25b. 书法艺术 - 永字八法 (竖屏) */}
      <Composition
        id="CalligraphyArt-yong"
        component={CalligraphyArtTemplate}
        durationInFrames={600}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 26. 美术鉴赏 - 画廊展览 (横屏) */}
      <Composition
        id="ArtGallery-starryNight"
        component={ArtGalleryTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          imageUrl: staticFile('art-gallery/starry-night.jpg'),
        }}
      />
      <Composition
        id="ArtGallery-monaLisa"
        component={ArtGalleryTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          paintingTitle: '蒙娜丽莎',
          paintingTitleEn: 'Mona Lisa',
          artist: '列奥纳多·达·芬奇',
          year: '1503-1519',
          style: '文艺复兴盛期',
          imageUrl: staticFile('art-gallery/mona-lisa.jpg'),
          imageFit: 'contain' as const,
          colors: [
            { hex: '#4a3728', name: '赭石', percentage: 40 },
            { hex: '#8b7355', name: '熟褐', percentage: 25 },
            { hex: '#c4a882', name: '肤色', percentage: 20 },
            { hex: '#2d4a2a', name: '暗绿', percentage: 10 },
            { hex: '#1a1a2e', name: '暗影', percentage: 5 },
          ],
          compositionType: { type: 'golden', label: '黄金分割构图' },
          details: [
            { label: '晕涂法', description: '达·芬奇独创 sfumato 技法，色层间无明显边界' },
            { label: '微笑之谜', description: '嘴角的微妙弧度随观察角度变化' },
            { label: '空气透视', description: '远景色调偏蓝，模拟大气散射效果' },
          ],
          significance: '西方美术史最具辨识度的肖像画，达·芬奇对人体与自然的完美融合',
          funFact: '1911 年曾被盗，失窃期间比展出时吸引了更多参观者',
        }}
      />

      {/* 27. 音乐乐理 - 五线谱动画 (横屏) */}
      <Composition
        id="MusicTheory-cMajor"
        component={MusicTheoryTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 28. 历史大事件 - 电影时间线 (横屏) */}
      <Composition
        id="HistoryTimeline-communication"
        component={HistoryTimelineTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />
      <Composition
        id="HistoryTimeline-computing"
        component={HistoryTimelineTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          topic: '计算机发展简史',
          subtitle: 'History of Computing: From Abacus to AI',
          events: [
            { year: '约 2400 BC', title: '算盘', detail: '人类最早的计算工具', era: '古代', color: '#d4a847', icon: '🧮' },
            { year: '1837', title: '分析机', detail: '巴贝奇设计通用计算机概念', era: '近代', color: '#b87333', icon: '⚙️' },
            { year: '1946', title: 'ENIAC', detail: '第一台电子通用计算机', era: '电子时代', color: '#6b8e23', icon: '🖥️' },
            { year: '1969', title: 'UNIX', detail: '操作系统基石，影响至今', era: '软件时代', color: '#4a90d9', icon: '💾' },
            { year: '1976', title: 'Apple I', detail: '个人电脑时代开启', era: 'PC 时代', color: '#4a90d9', icon: '🍎' },
            { year: '1991', title: 'WWW', detail: '万维网诞生，信息平权', era: '互联网', color: '#2563eb', icon: '🌐' },
            { year: '2007', title: 'iPhone', detail: '移动互联网爆发', era: '移动时代', color: '#7c3aed', icon: '📱' },
            { year: '2022', title: 'ChatGPT', detail: '大语言模型走向大众', era: 'AI 时代', color: '#9b59b6', icon: '🤖' },
          ],
          conclusion: '从算盘到 AI，每一次计算革命都在重新定义人类的能力边界',
          timespan: '4400 年计算史',
        }}
      />

      {/* 29. 经济金融 - 财经仪表盘 (横屏) */}
      <Composition
        id="FinanceDashboard-aShare"
        component={FinanceDashboardTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 29b. 人工智能 - 深度学习神经网络 (横屏) */}
      <Composition
        id="AINetwork-deepLearning"
        component={AINetworkTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 30. 天文宇宙 - 星际探索 (竖屏) */}
      <Composition
        id="Astronomy-solarSystem"
        component={AstronomyTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 31. 哲学思辨 - 思维碰撞 (竖屏) */}
      <Composition
        id="PhilosophyDialectic-freeWill"
        component={PhilosophyDialecticTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />
      <Composition
        id="PhilosophyDialectic-trolley"
        component={PhilosophyDialecticTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          question: '功利主义 vs 义务论',
          conceptA: {
            label: '功利主义',
            icon: '⚖️',
            color: '#22c55e',
            arguments: ['追求最大多数人的最大幸福', '电车难题：拉拉杆救更多人', '结果才是判断善恶的标准'],
          },
          conceptB: {
            label: '义务论',
            icon: '📜',
            color: '#ef4444',
            arguments: ['有些行为本身就是错的', '不能把人当作手段', '尊重每个人的不可侵犯性'],
          },
          synthesis: '美德伦理学提供第三条路径：关键不在于规则或结果，而在于什么样的人会做出什么样的选择',
          quote: '人永远不应该被仅仅当作手段',
          quoteAuthor: '伊曼努尔·康德',
          reflection: '电车难题没有标准答案——但思考的过程本身就在塑造我们的道德直觉',
        }}
      />

      {/* 32. 健康医学 - 人体图解 (横屏) */}
      <Composition
        id="HealthAnatomy-cardiovascular"
        component={HealthAnatomyTemplate}
        durationInFrames={600}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ===== Batch 6: 扩展专业类型 ===== */}

      {/* 33. 语言学习 - 单词卡 (竖屏) */}
      <Composition
        id="LanguageCard-unprecedented"
        component={LanguageCardTemplate}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 34. 地理探索 - 地图标注 (横屏) */}
      <Composition
        id="GeographyMap-chinaSteps"
        component={GeographyMapTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 35. 法律常识 - 法条解读 (竖屏) */}
      <Composition
        id="LawExplainer-overtime"
        component={LawExplainerTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 36. 建筑设计 - 蓝图展开 (横屏) */}
      <Composition
        id="ArchitectureBlueprint-minimal"
        component={ArchitectureBlueprintTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 37. 烹饪美食 - 食谱卡片 (横屏) */}
      <Composition
        id="CookingRecipe-teriyaki"
        component={CookingRecipeTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 38. 摄影技巧 - 取景分析 (横屏) */}
      <Composition
        id="Photography-goldenHour"
        component={PhotographyTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 39. 体育科学 - 运动分析 (横屏) */}
      <Composition
        id="SportsAnalysis-sprint"
        component={SportsAnalysisTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 40. 数学可视化 - 函数世界 (竖屏) */}
      <Composition
        id="MathVisualization-trig"
        component={MathVisualizationTemplate}
        durationInFrames={540}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 41. 环境科学 - 气候变化与碳排放 (横屏) */}
      <Composition
        id="EnvironmentClimate-global"
        component={EnvironmentClimateTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 42. 化学实验室 - 分子可视化 (横屏) */}
      <Composition
        id="ChemistryLab-h2o"
        component={ChemistryLabTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 43. 生物科学 - DNA 双螺旋 (横屏) */}
      <Composition
        id="BiologyDNA-helix"
        component={BiologyDNATemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 44. 电影赏析 - 影评分析 (横屏) */}
      <Composition
        id="FilmCinematic-interstellar"
        component={FilmCinematicTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 45. 棋类博弈 - 国际象棋战术 (横屏) */}
      <Composition
        id="ChessStrategy-sicilian"
        component={ChessStrategyTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 46. 电子电路 - LED 电路原理 (横屏) */}
      <Composition
        id="ElectronicsCircuit-led"
        component={ElectronicsCircuitTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ===== Batch 8: 新增专业领域 ===== */}

      {/* 47. 海洋科学 - 深海探索 (竖屏) */}

      {/* 48. 量子物理 - 波粒二象性 (横屏) */}

      {/* 49. 传统文化 - 春节 (竖屏) */}

      {/* 50. 机器人技术 - 工业机械臂 (横屏) */}

      {/* 51. 植物学 - 光合作用 (竖屏) */}

      {/* 52. 网络安全 - 数据加密 (横屏) */}

      {/* 53. 设计原理 - 色彩理论 (横屏) */}

      {/* 54. 武术太极 - 太极拳 (竖屏) */}

      {/* ===== Batch 9: 新增专业领域 II ===== */}

      {/* 55. 考古学 - 古埃及文明 (横屏) */}

      {/* 56. 动物学 - 非洲草原生态 (竖屏) */}

      {/* 57. 气象学 - 台风形成 (横屏) */}

      {/* 58. 航空航天 - 长征五号 (横屏) */}

      {/* 59. 情绪管理 - 情绪智力 (竖屏) */}

      {/* 60. 经济学 - 供需均衡 (横屏) */}

      {/* 61. 瑜伽冥想 - 脉轮冥想 (竖屏) */}

      {/* 62. 地质学 - 地球构造 (竖屏) */}

      {/* ===== Batch 10: 新增专业领域 III ===== */}

      {/* 63. 茶道文化 - 中国茶艺 (竖屏) */}

      {/* 64. 数据科学 - 机器学习流程 (横屏) */}

      {/* 65. 微生物学 - 免疫响应 (横屏) */}

      {/* 66. 中医 - 五行经络 (竖屏) */}

      {/* 67. 星座天文 - 猎户座 (横屏) */}

      {/* 68. 3D打印 - 增材制造 (横屏) */}

      {/* 69. 人类学 - 文明演进 (竖屏) */}

      {/* 70. 交响乐 - 贝多芬第九 (横屏) */}

      {/* ===== Batch 11: 新增专业领域 IV ===== */}

      {/* 71. 营养学 - 健康膳食 (竖屏) */}

      {/* 72. 逻辑学 - 批判性思维 (横屏) */}

      {/* 73. 密码学 - 加密原理 (横屏) */}

      {/* 74. 农业科学 - 作物种植 (竖屏) */}

      {/* 75. 声学 - 声波原理 (横屏) */}

      {/* 76. 纺织时尚 - 面料科学 (竖屏) */}

      {/* 77. 概率博弈 - 囚徒困境 (横屏) */}

      {/* 78. 光学 - 棱镜折射 (横屏) */}

      {/* ===== Batch 12: 新增专业领域 V ===== */}

      {/* 79. 社会学 - 社会结构 (竖屏) */}

      {/* 80. 药理学 - 药物作用 (横屏) */}

      {/* 81. 火山学 - 火山喷发 (横屏) */}

      {/* 82. 汉字演变 - 文字学 (竖屏) */}

      {/* 83. 神经科学 - 大脑与神经元 (横屏) */}

      {/* 84. 葡萄酒鉴赏 - 品酒入门 (竖屏) */}

      {/* 85. 地图学 - 制图科学 (横屏) */}

      {/* 86. 折纸艺术 - 数学折纸 (竖屏) */}

      {/* ===== Batch 13: 新增专业领域 VI ===== */}

      {/* 87. 能源科学 - 可持续发展 (横屏) */}

      {/* 88. 古生物学 - 恐龙时代 (竖屏) */}

      {/* 89. 符号学 - 视觉传达 (竖屏) */}

      {/* 90. 法医科学 - 刑事鉴定 (横屏) */}

      {/* 91. 字体排版 - 文字设计 (横屏) */}

      {/* 92. 蜜蜂科学 - 养蜂学 (竖屏) */}

      {/* 93. 结绳学 - 绳结艺术 (竖屏) */}

      {/* 94. 水文学 - 水循环 (横屏) */}

      {/* ===== Batch 14: 新增专业领域 VII ===== */}

      {/* 95. 睡眠科学 - 生物钟 (竖屏) */}

      {/* 96. 材料科学 - 晶体结构 (横屏) */}

      {/* 97. 神话学 - 世界神话 (竖屏) */}

      {/* 98. 城市规划 - 智慧城市 (横屏) */}

      {/* 99. 手语 - 无障碍沟通 (竖屏) */}

      {/* 100. 流体力学 - 伯努利原理 (横屏) */}

      {/* 101. 调香学 - 香水科学 (竖屏) */}

      {/* 102. 区块链 - 去中心化 (横屏) */}

      {/* ===== Batch 15: 新增专业领域 VIII ===== */}

      {/* 103. 陶瓷艺术 - 烧制工艺 (竖屏) */}

      {/* 104. 认知科学 - 记忆机制 (横屏) */}

      {/* 105. 铁路工程 - 高速列车 (横屏) */}

      {/* 106. 造纸术 - 印刷演变 (竖屏) */}

      {/* 107. 博弈论 - 纳什均衡 (横屏) */}

      {/* 108. 发酵科学 - 微生物 (竖屏) */}

      {/* 109. 建筑风格 - 风格演变 (横屏) */}

      {/* 110. 宝石学 - 矿物鉴定 (竖屏) */}

      {/* ===== Batch 16: 新增专业领域 IX ===== */}

      {/* 111. 香料学 - 贸易之路 (竖屏) */}

      {/* 112. 量子计算 - 量子比特 (横屏) */}

      {/* 113. 皮影戏 - 木偶艺术 (竖屏) */}

      {/* 114. 桥梁工程 - 结构力学 (横屏) */}

      {/* 115. 咖啡学 - 冲泡科学 (竖屏) */}

      {/* 116. 动画原理 - 运动规律 (横屏) */}

      {/* 117. 丝绸之路 - 文明交流 (横屏) */}

      {/* 118. 草药学 - 植物药理 (竖屏) */}

      {/* ===== Batch 17: 新增专业领域 X ===== */}

      {/* 119. 太阳系 - 行星探索 (横屏) */}

      {/* 120. 舞蹈艺术 - 身体语言 (竖屏) */}

      {/* 121. 玻璃工艺 - 光学艺术 (竖屏) */}

      {/* 122. 航空学 - 飞行原理 (横屏) */}

      {/* 123. 巧克力学 - 可可科学 (横屏) */}
      <Composition
        id="Chocolate-cocoaScience"
        component={ChocolateTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 124. 分形数学 - 自然之美 (横屏) */}

      {/* 125. 博物馆学 - 展览设计 (横屏) */}

      {/* 126. 风筝文化 - 空气动力 (竖屏) */}

      {/* ===== Batch 18: 新增专业领域 XI ===== */}

      {/* 127. 剪纸艺术 - 民间工艺 (竖屏) */}

      {/* 128. 潜艇工程 - 深海探索 (横屏) */}

      {/* 129. 香道文化 - 沉香鉴赏 (竖屏) */}

      {/* 130. 激光技术 - 光学应用 (横屏) */}

      {/* 131. 土壤学 - 地球生态 (竖屏) */}

      {/* 132. 扑克数学 - 概率策略 (横屏) */}

      {/* 133. 漫画史 - 视觉叙事 (横屏) */}

      {/* 134. 灯笼文化 - 光影艺术 (竖屏) */}

      {/* ===== Batch 19: 新增专业领域 XII ===== */}

      {/* 135. 刺绣艺术 - 针法技艺 (竖屏) */}

      {/* 136. 迷宫算法 - 路径搜索 (横屏) */}

      {/* 137. 盆景艺术 - 造型美学 (竖屏) */}

      {/* 138. 核物理 - 核能利用 (横屏) */}

      {/* 139. 方言学 - 语言多样性 (竖屏) */}

      {/* 140. 钟表学 - 精密机械 (横屏) */}

      {/* 141. 染织艺术 - 色彩科学 (竖屏) */}

      {/* 142. 虚拟现实 - 沉浸技术 (横屏) */}

      {/* ===== Batch 20: 新增专业领域 XIII ===== */}

      {/* 143. 魔术学 - 视觉幻象 (竖屏) */}

      {/* 144. 通信技术 - 信号传输 (横屏) */}

      {/* 145. 烟花学 - 化学之美 (竖屏) */}

      {/* 146. 3D建模 - 数字雕塑 (横屏) */}

      {/* 147. 历法学 - 时间计量 (竖屏) */}

      {/* 148. 雷达技术 - 电磁探测 (横屏) */}

      {/* 149. 乐器制作 - 声学原理 (竖屏) */}

      {/* 150. 传统射箭 - 弹道学 (横屏) */}

      {/* ===== Batch 21: 新增专业领域 XIV ===== */}

      {/* 151. 货币学 - 货币演变 (竖屏) */}

      {/* 152. 全息技术 - 光学成像 (横屏) */}

      {/* 153. 种子库 - 生物多样性 (竖屏) */}

      {/* 154. 风力发电 - 清洁能源 (横屏) */}

      {/* 155. 面具文化 - 戏剧表演 (竖屏) */}

      {/* 156. 卫星技术 - 太空通信 (横屏) */}

      {/* 157. 金缮修复 - 残缺之美 (竖屏) */}

      {/* 158. 密码机 - 信息战争 (横屏) */}

      {/* ===== Batch 22: 新增专业领域 XV ===== */}

      {/* 159. 日晷学 - 古代计时 (竖屏) */}

      {/* 160. 水力发电 - 大坝工程 (横屏) */}

      {/* 161. 蝴蝶学 - 完全变态 (竖屏) */}

      {/* 162. 传热学 - 热力学 (横屏) */}

      {/* 163. 算盘学 - 古代计算 (竖屏) */}

      {/* 164. 霓虹灯艺术 - 气体放电 (横屏) */}

      {/* 165. 指南针 - 导航术 (竖屏) */}

      {/* 166. 密室逃脱 - 谜题设计 (横屏) */}

      {/* ===== Batch 23: 新增专业领域 XVI ===== */}

      {/* 167. 永续农业 - 生态设计 (竖屏) */}

      {/* 168. 洞穴探险 - 地下世界 (横屏) */}

      {/* 169. 花道 - 插花艺术 (竖屏) */}

      {/* 170. 半导体 - 芯片技术 (横屏) */}

      {/* 171. 丝绸工艺 - 蚕桑文化 (竖屏) */}

      {/* 172. 钟摆物理 - 谐振动 (横屏) */}

      {/* 173. 纸模型 - 结构力学 (竖屏) */}

      {/* 174. 电动汽车 - 新能源出行 (横屏) */}

      {/* ===== Batch 24: 新增专业领域 XVII ===== */}

      {/* 175. 蘑菇学 - 真菌王国 (横屏) */}
      <Composition
        id="Mushroom-mycology"
        component={MushroomTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 176. 火箭推进 - 航天动力 (横屏) */}
      <Composition
        id="RocketPropulsion-spacePower"
        component={RocketPropulsionTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 177. 漆器艺术 - 大漆工艺 (横屏) */}
      <Composition
        id="Lacquerware-artCraft"
        component={LacquerwareTemplate}
        durationInFrames={900}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 178. 陀螺仪 - 角动量 (横屏) */}
      <Composition
        id="Gyroscope-angularMomentum"
        component={GyroscopeTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 179. 马赛克艺术 - 镶嵌工艺 (横屏) */}
      <Composition
        id="Mosaic-tessellation"
        component={MosaicTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 180. 海水淡化 - 水资源 (横屏) */}
      <Composition
        id="Desalination-waterResource"
        component={DesalinationTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 181. 竹文化 - 竹工程 (横屏) */}
      <Composition
        id="Bamboo-culture"
        component={BambooTemplate}
        durationInFrames={300}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 182. 供应链 - 物流科学 (横屏) */}
      <Composition
        id="SupplyChain-logistics"
        component={SupplyChainTemplate}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 183. 深海探索 - 海洋生物 (横屏) */}
      <Composition
        id="DeepOcean-marine"
        component={DeepOceanTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ===== Batch 25: 知识类五大分类新模板 ===== */}

      {/* 184. 经典力学 - 牛顿定律 (横屏) · 基础学科与学术教育 */}
      <Composition
        id="ClassicalMechanics-newtonLaws"
        component={ClassicalMechanicsTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 185. 产品设计方法论 (横屏) · 职业技能与实操技术 */}
      <Composition
        id="ProductDesign-methodology"
        component={ProductDesignTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 186. 相对论与时空 (横屏) · 硬核科普与通识教育 */}
      <Composition
        id="Relativity-spacetime"
        component={RelativityTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 187. 第一性原理思维 (横屏) · 思维启发与个人成长 */}
      <Composition
        id="FirstPrinciples-thinking"
        component={FirstPrinciplesTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 188. 马斯洛需求层次 (横屏) · 心理学 */}
      <Composition
        id="MaslowNeeds-hierarchy"
        component={MaslowNeedsTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 新增模板 · 基础学科与学术教育 ==================== */}

      {/* 189. 统计学与数据分析 (横屏) · 基础学科与学术教育 */}
      <Composition
        id="Statistics-dataAnalysis"
        component={StatisticsTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 190. 线性代数与向量空间 (横屏) · 基础学科与学术教育 */}
      <Composition
        id="LinearAlgebra-vectorSpaces"
        component={LinearAlgebraTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 191. 有机化学与分子世界 (横屏) · 基础学科与学术教育 */}
      <Composition
        id="OrganicChem-molecularWorld"
        component={OrganicChemTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 192. 进化生物学与自然选择 (横屏) · 基础学科与学术教育 */}
      <Composition
        id="EvolutionBio-naturalSelection"
        component={EvolutionBioTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 新增模板 · 职业技能与实操技术 ==================== */}

      {/* 193. 演讲与口才艺术 (横屏) · 职业技能与实操技术 */}
      <Composition
        id="PublicSpeaking-rhetoric"
        component={PublicSpeakingTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 194. 项目管理方法论 (横屏) · 职业技能与实操技术 */}
      <Composition
        id="ProjectMgmt-methodology"
        component={ProjectMgmtTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 195. 木工与榫卯技艺 (横屏) · 职业技能与实操技术 */}
      <Composition
        id="WoodworkingCraft-joinery"
        component={WoodworkingCraftTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 196. 视频剪辑与后期制作 (横屏) · 职业技能与实操技术 */}
      <Composition
        id="VideoEditing-postProduction"
        component={VideoEditingTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 新增模板 · 硬核科普与通识教育 ==================== */}

      {/* 197. 疫苗与免疫系统 (横屏) · 硬核科普与通识教育 */}
      <Composition
        id="VaccineImmune-immuneSystem"
        component={VaccineImmuneTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 198. 黑洞与时空弯曲 (横屏) · 硬核科普与通识教育 */}
      <Composition
        id="BlackHole-spacetime"
        component={BlackHoleTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 199. 大脑可塑性与神经重塑 (横屏) · 硬核科普与通识教育 */}
      <Composition
        id="BrainPlasticity-neuralRemodeling"
        component={BrainPlasticityTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 200. 超导体与量子锁定 (横屏) · 硬核科普与通识教育 */}
      <Composition
        id="Superconductor-quantumLocking"
        component={SuperconductorTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 新增模板 · 泛知识与生活百科 ==================== */}

      {/* 201. 个人理财与投资基础 (横屏) · 泛知识与生活百科 */}
      <Composition
        id="PersonalFinance-investmentBasics"
        component={PersonalFinanceTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 202. 室内植物养护科学 (横屏) · 泛知识与生活百科 */}
      <Composition
        id="HousePlant-careSscience"
        component={HousePlantTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 203. 野外生存技巧 (横屏) · 泛知识与生活百科 */}
      <Composition
        id="SurvivalSkills-wilderness"
        component={SurvivalSkillsTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 204. 宠物饲养与行为科学 (横屏) · 泛知识与生活百科 */}
      <Composition
        id="PetCare-animalBehavior"
        component={PetCareTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 新增模板 · 思维启发与个人成长 ==================== */}

      {/* 205. 批判性思维与逻辑分析 (横屏) · 思维启发与个人成长 */}
      <Composition
        id="CriticalThinking-logicAnalysis"
        component={CriticalThinkingTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 206. 习惯养成科学 (横屏) · 思维启发与个人成长 */}
      <Composition
        id="HabitFormation-science"
        component={HabitFormationTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 207. 记忆术与高效学习 (横屏) · 思维启发与个人成长 */}
      <Composition
        id="MemoryTech-efficientLearning"
        component={MemoryTechTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 新增模板 · 心理学 ==================== */}

      {/* 208. 积极心理学与幸福科学 (横屏) · 心理学 */}
      <Composition
        id="PositivePsych-happiness"
        component={PositivePsychTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 209. 色彩心理学与感知 (横屏) · 心理学 */}
      <Composition
        id="ColorPsych-perception"
        component={ColorPsychTemplate}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* ==================== 2026-05 新增：六大分类知识视频模板 (Batch 26) ==================== */}

      {/* 210. 神经科学课堂 · 大脑是如何思考的 (横屏) · 基础学科与学术教育 */}
      <Composition
        id="NeuroScienceLecture-brainThinks"
        component={NeuroScienceLectureTemplate}
        durationInFrames={600}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 211. 数据库索引原理 · 从全表扫描到毫秒响应 (横屏) · 职业技能与实操技术 */}
      <Composition
        id="DatabaseIndexExpert-bplusTree"
        component={DatabaseIndexExpertTemplate}
        durationInFrames={660}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 212. 宇宙尺度之旅 · 从夸克到可观测宇宙 (竖屏) · 硬核科普与通识教育 */}
      <Composition
        id="CosmicScaleJourney-quarkToUniverse"
        component={CosmicScaleJourneyTemplate}
        durationInFrames={770}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{}}
      />

      {/* 213. 关键 3 分钟 · 家庭急救手册 (横屏) · 泛知识与生活百科 */}
      <Composition
        id="EmergencyFirstAid-criticalThreeMinutes"
        component={EmergencyFirstAidTemplate}
        durationInFrames={770}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />

      {/* 214. 复利效应的奇迹 · 时间与坚持的力量 (横屏) · 思维启发与个人成长 */}
      <Composition
        id="CompoundEffectMagic-timeAndPersistence"
        component={CompoundEffectMagicTemplate}
        durationInFrames={600}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{}}
      />
    </>
  );
};
